import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { parse, getValidInboxSort } from '../../util/urlHelpers';
import {
  getSupportedProcessesInfo,
} from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchLastMessagesForCappedInquiries } from '../../util/inboxAttention';

export const INBOX_PAGE_SIZE = 100;
const DEFAULT_INBOX_SORT = { sort: 'lastMessageAt,lastTransitionedAt,createdAt' };

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Slice ================ //

const inboxPageSlice = createSlice({
  name: 'InboxPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
    // D34: newest message per capped inquiry chat (its include misses it), by tx uuid
    lastMessageOverrides: {},
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const transactions = action.payload.data.data;
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(transactions);
        state.pagination = action.payload.data.meta;
        state.lastMessageOverrides = action.payload.lastMessageOverrides || {};
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
      });
  },
});

export default inboxPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = ({ params, search }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { page = 1, sort } = parse(search);
  const processNames = getSupportedProcessesInfo().map(p => p.name);
  const sortQueryParam = getValidInboxSort(sort);
  const sortParams =
    sortQueryParam && Object.keys(sortQueryParam).length > 0 ? sortQueryParam : DEFAULT_INBOX_SORT;

  const apiQueryParams = {
    processNames,
    include: [
      'listing',
      'provider',
      'provider.profile',
      'provider.profileImage',
      'customer',
      'customer.profile',
      'customer.profileImage',
      'booking',
      'messages',
      // D34: the inquiry turn is the last message's sender (already-included provider/customer)
      'messages.sender',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      // D34: «Начато {date}» on rows of deleted listings
      'createdAt',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType', 'deleted'],
    'fields.user': [
      'profile.publicData.specialistId',
      'profile.displayName',
      'profile.abbreviatedName',
      // D45: a deleted inquiry listing's role group follows the counterparty's userType
      'profile.publicData.userType',
      'deleted',
      'banned',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    // D37: a deleted listing's row quotes its oldest message
    'fields.message': ['createdAt', 'content'],
    page,
    perPage: INBOX_PAGE_SIZE,
    ...sortParams,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      let transactions = [];
      try {
        transactions = denormalisedResponseEntities(response);
      } catch (e) {
        // Without denormalised entities no chat can be detected as capped.
      }
      return fetchLastMessagesForCappedInquiries(sdk, transactions).then(lastMessageOverrides => ({
        ...response,
        lastMessageOverrides,
      }));
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('InboxPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for the thunk
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  return Promise.all([
    dispatch(loadDataThunk({ params, search })),
    // D34: the menu count and the sidebar count on this screen come from the same moment
    dispatch(fetchCurrentUserNotifications()).catch(() => null),
  ]);
};
